import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

import { hasAcceptedLatestTerms } from '@freelancerepublik/models-core/TermsList.core';
import { getInstance } from '@/plugins/auth';
import { isAuthenticated, watchAuthLoading, setHasLoginRoute } from '@/infra/authGuard';

import { ENV } from './env';

setHasLoginRoute(true);

const components = {
  AccountNotFoundPage: () => import('./ui/pages/AccountNotFoundPage.vue'),
  AuthCallbackPage: () => import('./ui/pages/AuthCallbackPage.vue'),
  CallToActionPage: () => import('./ui/pages/CallToActionPage.vue'),
  ForgotPasswordPage: () => import('./ui/pages/ForgotPasswordPage.vue'),
  LoginPage: () => import('./ui/pages/LoginPage.vue'),
  ClientOpportunitiesPage: () => import('./ui/pages/ClientOpportunitiesPage.vue'),
  ClientOpportunityPage: () => import('./ui/pages/ClientOpportunityPage.vue'),
  CompanyPage: () => import('./ui/pages/CompanyPage.vue'),
  CommunityPage: () => import('./ui/pages/CommunityPage.vue'),
  CandidateMarketSituationPage: () => import('./ui/pages/CandidateMarketSituationPage.vue'),
  CandidateOpportunitiesPage: () => import('./ui/pages/CandidateOpportunitiesPage.vue'),
  CandidatePreferencesPage: () => import('./ui/pages/CandidatePreferencesPage.vue'),
  FirstUseClientPage: () => import('./ui/pages/FirstUseClientPage.vue'),
  FirstUseCandidatePage: () => import('./ui/pages/FirstUseCandidatePage.vue'),
  CandidatePublicProfilePage: () => import('./ui/pages/CandidatePublicProfilePage.vue'),
  CandidateReferencesPage: () => import('./ui/pages/CandidateReferencesPage.vue'),
  CandidateResumePage: () => import('./ui/pages/CandidateResumePage.vue'),
  InvitationPage: () => import('./ui/pages/InvitationPage.vue'),
  SignupChoicePage: () => import('./ui/pages/SignupChoicePage.vue'),
  SignupPage: () => import('./ui/pages/SignupPage.vue'),
  ManagerRecommendationPage: () => import('./ui/pages/ManagerRecommendationPage.vue'),
  ProfilePage: () => import('./ui/pages/ProfilePage.vue'),
  ProposalKeySkillsActionPage: () => import('./ui/pages/ProposalKeySkillsActionPage.vue'),
  UserDashboardProxyPage: () => import('./ui/pages/UserDashboardProxyPage.vue'),
  UserOpportunitiesProxyPage: () => import('./ui/pages/UserOpportunitiesProxyPage.vue'),
};

export const guards = {
  isAuthenticated,
  isAnonymous(to, from, next) {
    watchAuthLoading(next, $auth => {
      if (!$auth.loggedUser) {
        return next();
      }

      next({ name: 'dashboard' });
    });
  },
};

export const routes: RouteRecordRaw[] = [
  {
    path: '/no-account',
    name: 'accountNotFound',
    component: components.AccountNotFoundPage,
  },
  {
    path: '/freelance-market-situation-edit',
    name: 'CandidateMarketSituationEdit',
    component: components.CallToActionPage,
  },
  {
    path: '/mission-selection-freelance-answer',
    name: 'MissionSelectionFreelanceAnswer',
    component: components.CallToActionPage,
  },
  {
    path: '/proposal-key-skills/:proposalId',
    name: 'KeySkillsPage',
    component: components.ProposalKeySkillsActionPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/welcome/freelance/description',
    name: 'suFreelance',
    component: components.SignupPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/welcome/freelance',
    name: 'fuFreelance',
    component: components.FirstUseCandidatePage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/welcome/company/description',
    name: 'suClient',
    component: components.SignupPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/welcome/client',
    name: 'fuClient',
    component: components.FirstUseClientPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/invitation/:token',
    name: 'invitation',
    component: components.InvitationPage,
    props: true,
  },
  {
    path: '/login',
    name: 'login',
    component: components.LoginPage,
    beforeEnter: guards.isAnonymous,
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: components.ForgotPasswordPage,
    beforeEnter: guards.isAnonymous,
  },
  {
    path: '/signup',
    name: 'signup',
    component: components.SignupChoicePage,
    beforeEnter: guards.isAnonymous,
  },
  {
    path: '/signup/freelance',
    name: 'signupFreelance',
    component: components.SignupPage,
    beforeEnter: guards.isAnonymous,
  },
  {
    path: '/signup/client',
    name: 'signupClient',
    component: components.SignupPage,
    beforeEnter: guards.isAnonymous,
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    alias: ['/'],
    component: components.UserDashboardProxyPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/cv',
    name: 'freelanceCV',
    component: components.CandidateResumePage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/profile',
    name: 'profile',
    component: components.ProfilePage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/situation',
    name: 'situation',
    component: components.CandidateMarketSituationPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/references',
    name: 'references',
    component: components.CandidateReferencesPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/missions',
    name: 'missions',
    component: components.UserOpportunitiesProxyPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/company',
    name: 'company',
    component: components.CompanyPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/preferences',
    name: 'missionPreferences',
    component: components.CandidatePreferencesPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/community',
    name: 'community',
    component: components.CommunityPage,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/freelance-review/:missionId/:freelanceId',
    name: 'freelancePublicProfile',
    component: components.CandidatePublicProfilePage,
    props: true,
  },
  {
    path: '/manager-recommendation/:freelanceId/:recommendationId',
    name: 'managerRecommendationPage',
    component: components.ManagerRecommendationPage,
    props: true,
  },
  {
    path: '/mission/:missionId',
    name: 'opportunityDetails',
    component: components.ClientOpportunityPage,
    props: true,
    beforeEnter: guards.isAuthenticated,
  },
  {
    path: '/loading',
    name: 'loginCallback',
    component: components.AuthCallbackPage,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

export const createAppRouter = ({ sentry, modal, gtm }) => {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: (to, from, savedPosition) => {
      if (savedPosition) return savedPosition;
      return { left: 0, top: 0 };
    },
  });

  router.beforeEach(async (to, from, next) => {
    modal?.hide();
    next();
  });

  router.afterEach(async to => {
    try {
      const $auth = getInstance();
      const loggedUser = $auth.loggedUser;

      gtm?.trackPageView(to.fullPath, document.title, loggedUser !== null);
      if (loggedUser) {
        if (ENV.PAGEVIEWS_FUNCTION) {
          const searchParams = new URLSearchParams({
            userId: loggedUser._id as string,
            page: window.location.href,
          });
          await fetch(`${ENV.PAGEVIEWS_FUNCTION}?${searchParams}`);
        }

        const termsList = loggedUser.termsList;

        if (!hasAcceptedLatestTerms(termsList)) {
          modal?.show('userTerms');
        }
      }
    } catch (err) {
      sentry.captureException(err);
    }

    document.dispatchEvent(new Event('navigation-complete'));
  });

  return router;
};
